import React, { memo, useContext, useState } from "react";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  BacktestingTimeframe,
  LanguageNameSpaces,
  MarketplaceSubGroup,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { buildMarketPlaceGroupEvent } from "utils/analytics";
import { getGroupExtraDetailsRoute } from "utils/routes";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { NEW_LOOK_PHONE_GUTTER, phoneMaxWidth } from "common/NewLook/layout.ts";
import { GROUP_CARD_ID } from "testIds.ts";
import Tooltip from "common/Tooltips/Tooltip";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { ScoreRing } from "features/newMarketplace/NewLook/NewLookScoreBadge";
import { RiskGauge } from "features/newMarketplace/NewLook/NewLookRiskMeter";
import useNewLookRisk from "features/newMarketplace/NewLook/useNewLookRisk";
import PnlChart from "features/newMarketplace/NewLook/PnlChart.tsx";
import useNetPnlSeries, {
  PnlPoint,
} from "features/newMarketplace/NewLook/useNetPnlSeries.ts";
import {
  GroupLogo,
  GroupRatingInline,
  GroupTagPills,
  PnlValue,
  PriceDollars,
  TrialLabel,
} from "features/newMarketplace/NewLook/NewLookGroupParts";
import { formatDateLabel } from "@shared/utils/date";

interface NewLookGroupCardProps {
  group: MarketplaceSubGroup;
  isOnboarding?: boolean;
  isTopTrader?: boolean;
  openScoreModal?: (group: MarketplaceSubGroup) => void;
  index: number;
  onCardClick?: () => void;
  /** Inside the "Top rated" panel: no own border, the panel frames it. */
  featured?: boolean;
}

/**
 * A marketplace channel on a panel: who it is (with its score), three key
 * figures in a well, the month's net PnL as a chart that runs down to the
 * footer, and a quiet footer with its status chips, trial and price level.
 */
const NewLookGroupCard = ({
  group,
  isOnboarding,
  openScoreModal,
  index,
  onCardClick,
  featured,
}: NewLookGroupCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();
  const risk = useNewLookRisk(group.backtesting_score_breakdown);

  const onGroupClick = () => {
    if (onCardClick) return onCardClick();
    analytics?.marketplaceGroupClicked(
      buildMarketPlaceGroupEvent(group, index + 1)
    );
    history.push(
      getGroupExtraDetailsRoute(location.pathname, group.group_id.toString())
    );
  };

  const getPnlStat = (timeframe: BacktestingTimeframe) => (
    <Stat>
      <StatLabel>{t("card.pnl", { months: timeframe / 30 })}</StatLabel>
      <StatPnl
        value={getNetPnlChange(group, timeframe, displayCurrency)}
        role="figureLarge"
      />
    </Stat>
  );

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onGroupClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget)
          onGroupClick();
      }}
      data-testid={GROUP_CARD_ID}
      $featured={featured}
    >
      <Head>
        <GroupLogo
          image={group.image_path}
          name={group.name}
          size={isMobile ? 36 : 40}
        />
        <Identity>
          <Tooltip title={group.name} placement="topLeft">
            <Name $wrap={isOnboarding}>{group.name}</Name>
          </Tooltip>
          <GroupRatingInline
            avgStars={group.avg_stars}
            numReviewers={group.num_of_reviewers}
          />
        </Identity>
        <ScoreRing
          score={group.backtesting_score}
          size={isMobile ? 40 : 48}
          onClick={openScoreModal ? () => openScoreModal(group) : undefined}
        />
      </Head>

      <Stats>
        {getPnlStat(180)}
        {getPnlStat(90)}
        <Stat>
          <StatLabel>{t("table.columns.risk")}</StatLabel>
          <RiskRow title={risk.label}>
            <RiskGauge level={risk.level} />
            <RiskValue style={{ color: risk.color }}>
              {risk.shortLabel ?? "–"}
            </RiskValue>
          </RiskRow>
        </Stat>
      </Stats>

      <CardChart
        group={group}
        height={isMobile ? 44 : undefined}
        inset={featured}
      />

      <Footer>
        <GroupTagPills group={group} />
        <FooterEnd>
          <TrialLabel
            hasFreeTrial={group.has_free_trial}
            isFree={group.is_free}
          />
          <PriceDollars priceNumber={group.price_number} />
        </FooterEnd>
      </Footer>
    </Card>
  );
};

export default memo(NewLookGroupCard);

/** The month's net PnL: the headline follows the crosshair. */
const CardChart = ({
  group,
  height,
  inset,
}: {
  group: MarketplaceSubGroup;
  height?: number;
  /** Keep the line inside the content padding (side-by-side cells). */
  inset?: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { points, change } = useNetPnlSeries(group, 30, 30);
  const [hovered, setHovered] = useState<PnlPoint>();

  return (
    <Chart>
      <ChartHeader>
        <StatLabel>
          {t("card.net_pnl_1m")}
          {hovered && (
            <HoverDate>
              {formatDateLabel(moment.unix(hovered.time), "MMM D")}
            </HoverDate>
          )}
        </StatLabel>
        <PnlValue value={hovered ? hovered.value : change} role="figure" />
      </ChartHeader>
      <ChartArea $height={height} $inset={inset}>
        <PnlChart
          points={points}
          change={change}
          ariaLabel={t("chart.aria_label", { range: "1M" })}
          height={height}
          onActiveChange={setHovered}
          hideEndDot={true}
        />
      </ChartArea>
    </Chart>
  );
};

const Card = styled.div<{ $featured?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition: border-color 120ms;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
  ${(p) =>
    !p.$featured &&
    css`
      box-shadow: ${p.theme.shadows.panel};
      transition: border-color 120ms, box-shadow 160ms, transform 160ms;
      &:hover {
        transform: translateY(-2px);
        ${p.theme.isDarkMode
          ? `border-color: ${getNewLookTokens(p.theme).hairlineStrong};`
          : `box-shadow: 0 8px 20px rgba(16, 24, 40, 0.1);`}
      }
      @media (prefers-reduced-motion: reduce) {
        &:hover {
          transform: none;
        }
      }
    `}
  ${(p) =>
    p.$featured &&
    css`
      border: 0;
      border-radius: 0;
      background: transparent;
      transition: background-color 120ms;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${getNewLookTokens(p.theme).focus};
      }
    `}
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 20px 24px 0;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 16px ${NEW_LOOK_PHONE_GUTTER}px 0;
  }
`;
const Identity = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const Name = styled.div<{ $wrap?: boolean }>`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: ${(p) => (p.$wrap ? "normal" : "nowrap")};
  overflow-wrap: anywhere;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.cardTitle};
  }
`;
const Stats = styled.div`
  container-type: inline-size;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(max-content, 1fr);
  margin: 20px 24px 0;
  @media (max-width: ${phoneMaxWidth}) {
    margin: 16px ${NEW_LOOK_PHONE_GUTTER}px 0;
  }
`;
const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 0 0 0 12px;
  &:first-child {
    padding-left: 0;
  }
  & + & {
    border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const StatLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const RiskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 1px;
`;
/** Never above 18 inside a card; the phone figure step on a phone. */
const phoneFigure = css`
  @container (max-width: 300px) {
    font-size: 17px;
    line-height: 24px;
  }
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.figure};
  }
`;
const statFigure = css`
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
`;
const StatPnl = styled(PnlValue)`
  && {
    ${statFigure};
    ${phoneFigure};
  }
`;
const RiskValue = styled.span`
  min-width: 0;
  ${newLookType.figure};
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Chart = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-top: 16px;
`;
const ChartHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 0 24px;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 0 ${NEW_LOOK_PHONE_GUTTER}px;
  }
`;
const HoverDate = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div<{ $height?: number; $inset?: boolean }>`
  flex: 1 1 ${(p) => p.$height ?? 64}px;
  min-height: ${(p) => p.$height ?? 64}px;
  margin-top: 8px;
  /* Side-by-side cells: an edge-to-edge line would read as one line broken
     at the dividers, so each chart stays inside its own cell. */
  ${(p) =>
    p.$inset &&
    css`
      padding: 0 24px 4px;
      @media (max-width: ${phoneMaxWidth}) {
        padding: 0 ${NEW_LOOK_PHONE_GUTTER}px 4px;
      }
    `}
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-height: 52px;
  padding: 14px 24px;
  margin-top: 10px;
  @media (max-width: ${phoneMaxWidth}) {
    min-height: 48px;
    padding: 12px ${NEW_LOOK_PHONE_GUTTER}px;
  }
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const FooterEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;
